/* ============================================================
   Assinatura: o que a loja diz, e o que isso vira aqui dentro

   Mora no core pelo mesmo motivo que a pontuação e o teto de caloria: a
   função que recebe o aviso da Play e a tela que mostra "sua assinatura
   vence dia tal" precisam da MESMA resposta. Duas cópias divergiriam, e
   a divergência aqui é cara — uma delas cobra de quem já cancelou, a
   outra libera quem não pagou.

   A regra de ouro deste arquivo: NADA aqui confia no cliente. O que
   entra é o que a API da Play respondeu ao SERVIDOR. O app nunca manda
   "sou assinante" — ele manda um token de compra, e quem pergunta à Play
   o que aquele token vale é a função.
   ============================================================ */

/**
 * Os produtos que valem assinatura, em lista FECHADA.
 *
 * Mesma disciplina das posições de anúncio: aceitar qualquer id que
 * chegue seria deixar um token de outro produto — de outro app do mesmo
 * projeto, ou de um produto de teste esquecido no console — virar
 * assinatura aqui dentro. O id é cadastrado no Play Console e não muda;
 * quando entrar o plano anual, ele entra nesta lista no mesmo commit em
 * que for criado lá.
 */
export const PRODUTOS_DE_ASSINATURA = ["nhomi.assinante.mensal"] as const;

export type ProdutoDeAssinatura = (typeof PRODUTOS_DE_ASSINATURA)[number];

/** O produto que a tela oferece hoje. Um só, até existir o anual. */
export const PRODUTO_PADRAO: ProdutoDeAssinatura = "nhomi.assinante.mensal";

export const produtoConhecido = (id: unknown): id is ProdutoDeAssinatura =>
  (PRODUTOS_DE_ASSINATURA as readonly string[]).includes(String(id));

/**
 * O estado da assinatura, com os nomes da Play traduzidos.
 *
 * A Play devolve `SUBSCRIPTION_STATE_*`; guardar a string dela aqui
 * deixaria o formato de um fornecedor vazar para dentro do banco e para
 * dentro da tela. Estes nomes são nossos, e é neles que o resto do
 * código pensa — quando entrar a App Store, ela cai nos MESMOS estados
 * em vez de inventar um segundo vocabulário.
 */
export type EstadoDaAssinatura =
  | "ativa"
  | "carencia"
  | "cancelada"
  | "pausada"
  | "suspensa"
  | "expirada"
  | "pendente";

/** De onde veio a cobrança. Existe hoje uma; a App Store entra aqui. */
export type OrigemDaAssinatura = "play" | "manual";

/**
 * O que a Play chama de estado, e o que nós chamamos.
 *
 * Estado que não estiver neste mapa vira `expirada` — o lado seguro de
 * errar. Um valor novo que o Google introduza não pode virar assinatura
 * de graça só porque este código é mais velho que ele.
 */
const DA_PLAY: Record<string, EstadoDaAssinatura> = {
  SUBSCRIPTION_STATE_ACTIVE: "ativa",
  SUBSCRIPTION_STATE_IN_GRACE_PERIOD: "carencia",
  SUBSCRIPTION_STATE_CANCELED: "cancelada",
  SUBSCRIPTION_STATE_PAUSED: "pausada",
  SUBSCRIPTION_STATE_ON_HOLD: "suspensa",
  SUBSCRIPTION_STATE_EXPIRED: "expirada",
  SUBSCRIPTION_STATE_PENDING: "pendente",
  SUBSCRIPTION_STATE_PENDING_PURCHASE_CANCELED: "expirada",
};

export const estadoDaPlay = (bruto: unknown): EstadoDaAssinatura =>
  DA_PLAY[String(bruto ?? "")] ?? "expirada";

/**
 * Os estados que dão direito ao que a assinatura promete.
 *
 * `cancelada` está aqui, e é o ponto que mais gera bug: cancelar na Play
 * NÃO corta o acesso na hora. A pessoa pagou o mês e o mês é dela — o
 * estado vira `cancelada` no instante do cancelamento e a assinatura só
 * morre na data de vencimento. Cortar no aviso de cancelamento seria
 * tirar o que já foi pago, e é exatamente o tipo de coisa que vira
 * reclamação na loja.
 *
 * `carencia` também dá direito, e pelo mesmo espírito: o cartão falhou,
 * o Google está tentando de novo, e quem está no meio de um desafio não
 * pode perder o acesso por causa de uma cobrança que ainda pode passar.
 *
 * `suspensa` (on hold) é o depois disso: as tentativas acabaram. Aí sim
 * corta.
 */
const COM_DIREITO: ReadonlySet<EstadoDaAssinatura> = new Set<EstadoDaAssinatura>([
  "ativa", "carencia", "cancelada",
]);

/**
 * Esta assinatura vale AGORA?
 *
 * Duas condições, e as duas precisam valer. O estado é o que a Play
 * disse da última vez que perguntamos; a data é a rede embaixo dele.
 *
 * A data importa porque o aviso pode não chegar. O Pub/Sub entrega "ao
 * menos uma vez", o que não é o mesmo que "sempre": um tópico mal
 * configurado, uma função fora do ar por uma hora, um aviso perdido — e
 * uma assinatura `cancelada` com vencimento em março ficaria valendo
 * para sempre, porque o único evento que a mataria era justamente o que
 * se perdeu. Comparar com o relógio faz ela morrer sozinha na data
 * certa, sem depender de ninguém avisar.
 *
 * `ate` vazio conta como vencido: ausência de data aqui é dado
 * incompleto, não licença eterna. (O oposto do `endDay` do desafio, onde
 * vazio significa "não acaba" — lá é escolha de quem criou, aqui é falha
 * de quem gravou.)
 */
export function temDireito(
  estado: unknown, ate: unknown, agora: Date = new Date(),
): boolean {
  if (!COM_DIREITO.has(estado as EstadoDaAssinatura)) return false;
  const vencimento = Date.parse(String(ate ?? ""));
  return Number.isFinite(vencimento) && vencimento > agora.getTime();
}

/**
 * O `plano` que vai para `users/{uid}`, a partir da assinatura.
 *
 * Existe para haver UM lugar que decide. A função que confirma a compra,
 * a que recebe o aviso da Play e o script da linha de comando chamam
 * esta mesma função — senão cada caminho inventa a sua regra e a pessoa
 * vira assinante por um e grátis pelo outro.
 */
export const planoDaAssinatura = (
  estado: unknown, ate: unknown, agora?: Date,
): "assinante" | "gratis" =>
  temDireito(estado, ate, agora) ? "assinante" : "gratis";

/**
 * Como explicar o estado para quem está olhando a própria conta.
 *
 * Sem jargão de fornecedor e sem culpa. "Suspensa" não diz "você não
 * pagou" — diz o que fazer, que é a única coisa acionável.
 */
export const EXPLICACAO: Record<EstadoDaAssinatura, string> = {
  ativa: "Sua assinatura está ativa.",
  carencia: "A última cobrança não passou. O Google vai tentar de novo — "
    + "seu acesso continua enquanto isso.",
  cancelada: "Cancelada. Você continua com acesso até o fim do período já pago.",
  pausada: "Pausada na Play Store. Volta quando você retomar por lá.",
  suspensa: "A cobrança não passou e as tentativas acabaram. "
    + "Atualize a forma de pagamento na Play Store para voltar.",
  expirada: "Sua assinatura terminou.",
  pendente: "Pagamento em análise. Assim que a Play confirmar, o acesso abre.",
};
